import { useCallback, useMemo } from 'react';
import { Paths } from 'type-fest';

import { usePaginatedQuery } from '@occc-app/hooks/usePaginatedQuery';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { useSearchParams } from '@occc-app/hooks/useSearchParams';
import {
  ExternalLoyaltyManualOfferLocation,
  ExternalLoyaltyManualOfferLocationFilter,
  GetExternalLoyaltyManualOfferLocationsQuery,
  useGetExternalLoyaltyManualOfferLocationsQuery,
} from '@occc/graphql';

import { ROWS_PER_PAGE } from '../../tobacco-api-offers.constants';

const PAGE_INFO_PATH =
  'externalLoyaltyManualOfferLocations.pageInfo' satisfies Paths<GetExternalLoyaltyManualOfferLocationsQuery>;

const mapEdgesToLocations = (
  data: GetExternalLoyaltyManualOfferLocationsQuery | undefined,
): ExternalLoyaltyManualOfferLocation[] | undefined => {
  if (!data) return undefined;

  return data.externalLoyaltyManualOfferLocations.edges.map(({ node }) => node);
};

const STORES_AFTER_PARAM = 'storesAfter';
const STORES_BEFORE_PARAM = 'storesBefore';

const STORES_SEARCH_PARAMS = {
  searchParamName: 'storesSearch',
  afterParamName: STORES_AFTER_PARAM,
  beforeParamName: STORES_BEFORE_PARAM,
};

export const useTobaccoApiOfferLocations = (manualOfferUuid: string | undefined) => {
  const [search, setSearch] = useSearchParams(STORES_SEARCH_PARAMS);

  const [paginationInfo, onPageChange] = usePaginationParams({
    pageSize: ROWS_PER_PAGE,
    afterParamName: STORES_AFTER_PARAM,
    beforeParamName: STORES_BEFORE_PARAM,
  });

  const filter: ExternalLoyaltyManualOfferLocationFilter | undefined = useMemo(
    () => (search ? { search } : undefined),
    [search],
  );

  const { data, loading, error, paginationProps } = usePaginatedQuery(
    useGetExternalLoyaltyManualOfferLocationsQuery,
    PAGE_INFO_PATH,
    ROWS_PER_PAGE,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: { manualOfferUuid: manualOfferUuid ?? '', filter },
      skip: !manualOfferUuid,
    },
    paginationInfo,
    onPageChange,
  );

  const locations = useMemo(() => mapEdgesToLocations(data), [data]);

  const handleSearch = useCallback(
    (value: string) => {
      setSearch(value || undefined);
    },
    [setSearch],
  );

  return {
    locations,
    loading,
    error,
    paginationProps,
    search: search ?? '',
    onSearch: handleSearch,
  };
};
